import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Palette, Upload, Loader2, Sparkles, ExternalLink, Building2, Info,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ProfilePhotoPicker } from "@/components/ProfilePhotoPicker";
import { useAuth } from "@/context/AuthContext";

const MAX_BLURB = 500;
const ALLOWED = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const CompanyBranding = () => {
    const { user, refresh } = useAuth();
    const [branding, setBranding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [blurb, setBlurb] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employer/branding");
            setBranding(data);
            setBlurb(data.culture_blurb || "");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!ALLOWED.includes(f.type)) { toast.error("Logo must be PNG, JPEG, WebP, or SVG."); return; }
        if (f.size > MAX_LOGO_BYTES) { toast.error("Logo must be under 2 MB."); return; }
        setLogoFile(f);
        setLogoPreview(URL.createObjectURL(f));
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const form = new FormData();
            form.append("culture_blurb", blurb);
            if (logoFile) form.append("logo", logoFile);
            await api.post("/employer/branding", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Company profile saved.");
            setLogoFile(null);
            setLogoPreview(null);
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    if (loading || !branding) {
        return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;
    }

    const currentLogoSrc = logoPreview || (branding.logo_url ? `${process.env.REACT_APP_BACKEND_URL || ""}${branding.logo_url}` : null);

    return (
        <div data-testid="employer-branding-page" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={save} className="lg:col-span-2 space-y-4">
                <div>
                    <p className="overline">Employer · Company Profile</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Company branding
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Give candidates a reason to click. Your logo and culture blurb show at the top of your public page
                        {" "}<Link to={`/companies/${branding.company_slug}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">/companies/{branding.company_slug} <ExternalLink size={11} className="inline" /></Link>
                        {" "}and next to every role card on the Simonara board.
                    </p>
                </div>

                {/* Logo */}
                <div className="card-soft p-5 space-y-4">
                    <div>
                        <Label className="text-sm font-semibold">Company logo</Label>
                        <p className="text-xs text-muted-foreground mt-1">PNG, JPEG, WebP, or SVG · under 2 MB · square works best</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="h-20 w-20 rounded-2xl bg-muted flex items-center justify-center overflow-hidden ring-1 ring-border">
                            {currentLogoSrc ? (
                                <img src={currentLogoSrc} alt="Company logo" data-testid="branding-logo-preview" className="h-full w-full object-cover" />
                            ) : (
                                <Building2 size={26} className="text-muted-foreground" />
                            )}
                        </div>
                        <div className="flex-1">
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                onChange={handleFile}
                                data-testid="branding-logo-input"
                                className="hidden"
                                id="branding-logo-file"
                            />
                            <label
                                htmlFor="branding-logo-file"
                                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted cursor-pointer"
                            >
                                <Upload size={13} />
                                {logoFile ? logoFile.name : (branding.logo_url ? "Replace logo" : "Upload logo")}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Hiring manager photo — Feb 2026 iter 91.
                    Personal avatar for the individual seat, separate from
                    the company logo above. Shows on outreach emails +
                    candidate-facing pages so talent can see the human
                    behind the invite. */}
                <div className="card-soft p-5 space-y-4" data-testid="branding-manager-photo">
                    <div>
                        <Label className="text-sm font-semibold">Your photo (hiring manager)</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                            Your face appears next to outreach emails + on candidate-facing surfaces.
                            Personal to you, not the company — the logo above is separate.
                        </p>
                    </div>
                    <ProfilePhotoPicker
                        url={user?.picture || ""}
                        onChange={() => refresh().catch(() => { /* header will pick it up on next nav */ })}
                        endpoint="/auth/profile-photo"
                        label=""
                        testIdPrefix="branding-manager-photo"
                        name={user?.name || ""}
                        email={user?.email || ""}
                        userId={user?.user_id || ""}
                    />
                </div>

                {/* Culture blurb */}
                <div className="card-soft p-5 space-y-3">
                    <div>
                        <Label htmlFor="blurb" className="text-sm font-semibold">Culture blurb</Label>
                        <p className="text-xs text-muted-foreground mt-1">One paragraph. Talk about mission, team energy, and what makes joining you different.</p>
                    </div>
                    <Textarea
                        id="blurb"
                        data-testid="branding-blurb"
                        rows={5}
                        maxLength={MAX_BLURB}
                        value={blurb}
                        onChange={(e) => setBlurb(e.target.value)}
                        placeholder="At Acme we're building the operating system for modern HR teams — small crew, high trust, ship-daily energy…"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{blurb.length}/{MAX_BLURB} characters</span>
                    </div>
                </div>

                <button type="submit" disabled={saving} data-testid="branding-save" className="btn-green disabled:opacity-60">
                    {saving ? <><Loader2 size={16} className="mr-2 inline animate-spin" /> Saving…</> : "Save company profile"}
                </button>
            </form>

            {/* Sidebar tips */}
            <aside className="lg:col-span-1">
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="card-soft p-5 sticky top-6">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Sparkles size={16} /> <span className="text-sm font-semibold">Why this matters</span>
                    </div>
                    <ul className="mt-3 space-y-3 text-sm text-muted-foreground">
                        <li><Palette size={13} className="inline mr-1.5 text-emerald-500" /> Branded pages get <strong className="text-foreground">2–3x higher apply rates</strong> vs. no-logo listings.</li>
                        <li><Info size={13} className="inline mr-1.5 text-emerald-500" /> Logo shows up on <em>every</em> job card, in Related Jobs, and on the /companies page.</li>
                        <li><Building2 size={13} className="inline mr-1.5 text-emerald-500" /> Your public profile URL is stable — safe to share on LinkedIn, in cold outreach, in emails.</li>
                    </ul>
                </motion.div>
            </aside>
        </div>
    );
};

export default CompanyBranding;
